import { useContext, useState } from 'react';
import { Flex, Box, Image, Icon, Tooltip } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigationConfig } from './useNavigationConfig';
import { useIsExternalUser } from '@/hooks/useIsExternalUser';
import { KulaTextLogo } from '@/assets/icons/colored';
import kulaLogo from '@/assets/icons/Logo.svg';
import { FlexAlign } from '@/assets/icons/nav-v2';
import NavigationContext from './context';
import { useKFlags } from '@/hooks/use-k-flags';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { NavActivityTracker } from './components/NavActivityTracker';
import { useModeSyncWithUrl } from './hooks/useModeSyncWithUrl';

export const NavItem = ({
  item,
  index,
  isCollapsed,
  wrapper: Wrapper = '',
}) => {
  const {
    component: Component,
    props,
    styles,
    isAllowedToExternalUsers,
  } = item;
  const isExternalUser = useIsExternalUser();

  const renderComponent = () => (
    <Component key={index} {...props} {...styles} isCollapsed={isCollapsed} />
  );

  return (
    <>
      {!isExternalUser &&
        item.isEnabled &&
        (Wrapper ? <Wrapper>{renderComponent()}</Wrapper> : renderComponent())}
      {isExternalUser &&
        isAllowedToExternalUsers &&
        item.isEnabled &&
        (Wrapper ? <Wrapper>{renderComponent()}</Wrapper> : renderComponent())}
    </>
  );
};

const NavItems = ({ config, styles, isCollapsed = false }) => {
  return (
    <Box {...styles}>
      {config?.map((item, index) => (
        <NavItem
          key={index}
          item={item}
          index={index}
          isCollapsed={isCollapsed}
        />
      ))}
    </Box>
  );
};

export const SideNavigation = () => {
  const [isFlexVisible, setFlexVisible] = useState(false);
  const { navItemsTopSection, navItemsMiddleSection, navBottomItems } =
    useNavigationConfig();
  const { isNavCollapsed, handleNavCollapse } = useContext(NavigationContext);
  const { ats_enabled } = useKFlags();
  useModeSyncWithUrl();

  // The selected item sits on the bare white nav surface; the lavender panels
  // before and after it round their facing corners, so they curve away and the
  // white row reads as a notch that merges with the content on the right.
  const { pathname } = useLocation();
  const activeIndex = navItemsMiddleSection.findIndex(
    (i) => i.props?.link && pathname.startsWith(i.props.link)
  );
  const hasNotch = activeIndex >= 0;
  const itemsBeforeSelected = hasNotch
    ? navItemsMiddleSection.slice(0, activeIndex)
    : navItemsMiddleSection;
  const selectedItem = hasNotch ? navItemsMiddleSection[activeIndex] : null;
  const itemsAfterSelected = hasNotch
    ? navItemsMiddleSection.slice(activeIndex + 1)
    : [];

  const isMobile = useIsMobile();

  useHotkeys(
    '[',
    () => {
      handleNavCollapse(!isNavCollapsed);
    },
    { enableOnTags: [] }
  );

  const renderOldSearchDesign = () => {
    return (
      <>
        {!isNavCollapsed ? (
          <Icon width="100px" height="30px" as={KulaTextLogo} />
        ) : (
          <Box position="relative">
            <Image
              src={kulaLogo}
              width="30px"
              height="30px"
              display={isFlexVisible ? 'none' : 'block'}
            />
            <Flex
              width="30px"
              height="30px"
              justifyContent="center"
              alignItems="center"
              bg="#F4F4F5"
              borderRadius="5px"
              display={!isFlexVisible ? 'none' : 'flex'}
              onClick={() => {
                handleNavCollapse(false);
                setFlexVisible(false);
              }}
            >
              <Tooltip
                label="Expand ( [ )"
                arrowSize={8}
                hasArrow
                placement="right"
                borderRadius="4px"
                variant="default"
              >
                <Flex justifyContent="center" alignItems="center">
                  <Icon
                    as={FlexAlign}
                    width="24px"
                    height="24px"
                    cursor="pointer"
                  />
                </Flex>
              </Tooltip>
            </Flex>
          </Box>
        )}
        {!isNavCollapsed && (
          <Tooltip
            label="Minimize ( [ )"
            arrowSize={8}
            hasArrow
            placement="right"
            borderRadius="4px"
            variant="default"
          >
            <Flex>
              <Icon
                as={FlexAlign}
                width="24px"
                height="24px"
                cursor="pointer"
                onClick={() => handleNavCollapse(true)}
              />
            </Flex>
          </Tooltip>
        )}
      </>
    );
  };

  return !isMobile ? (
    <NavigationContext.Consumer>
      {() => (
        <Flex
          direction="column"
          bg="#FFF"
          onMouseOver={() => setFlexVisible(true)}
          onMouseOut={() => setFlexVisible(false)}
        >
          <Flex
            as="aside"
            flexDir="column"
            height="100%"
            width={`${isNavCollapsed ? '58px' : '240px'}`}
            transition="width 0.25s"
            pt="0"
            pl="0"
            pr="8px"
            pb={'0'}
            overflow="hidden"
            background={'base-white'}
          >
            {/* Logo / search — only in the pre-ats nav; empty otherwise. */}
            {!ats_enabled && (
              <Box flexShrink={0}>
                <Flex
                  justifyContent={!isNavCollapsed ? 'space-between' : 'center'}
                  alignItems="center"
                >
                  {renderOldSearchDesign()}
                </Flex>
              </Box>
            )}
            {/* Everything scrolls as one. The selected item sits on the bare white
                surface between the lavender panel before it (which holds the top
                Notifications/Task group too) and the panel after it; their rounded
                corners curve away to frame it. */}
            <Flex
              flex="1"
              minH={0}
              mb="8px"
              direction="column"
              overflowY="auto"
              overflowX="hidden"
            >
              <Box
                flexShrink={0}
                bg="secondary-25"
                borderRadius="0 0 16px 0"
                px="4px"
                py="4px"
              >
                <NavItems
                  config={navItemsTopSection}
                  isCollapsed={isNavCollapsed}
                />
                {itemsBeforeSelected.length > 0 && (
                  <NavItems
                    config={itemsBeforeSelected}
                    isCollapsed={isNavCollapsed}
                  />
                )}
              </Box>
              {selectedItem && (
                <Box flexShrink={0}>
                  <NavItems
                    config={[selectedItem]}
                    isCollapsed={isNavCollapsed}
                  />
                </Box>
              )}
              <Flex
                flex="1"
                minH={0}
                direction="column"
                bg="secondary-25"
                borderRadius="0 16px 0 0"
                px="4px"
                py="4px"
              >
                <Box flex="1">
                  <NavItems
                    config={itemsAfterSelected}
                    isCollapsed={isNavCollapsed}
                  />
                </Box>
                <Box flexShrink={0}>
                  <NavItems
                    config={navBottomItems}
                    isCollapsed={isNavCollapsed}
                  />
                  {/* Activity tracker for bulk operations - gets data from context */}
                  <Box mt="12px">
                    <NavActivityTracker isCollapsed={isNavCollapsed} />
                  </Box>
                </Box>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      )}
    </NavigationContext.Consumer>
  ) : null;
};
